import { useState, useCallback, useRef, useMemo } from 'react';
import { observer, inject } from 'mobx-react';
import Table from '../../../components/table/Table';
import { fetch } from '../../../utils/api-wrapper';
import InvoicePreview from '../../../components/Invoice-preview';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSync } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../../components/modal.js';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../../components/loading-spinner/loading-spinner';
import FormTextField from '../../../components/form-textfield';

const columnNameKeys = {
  expense_preview: 'cc_statements.unmatched_expenses.column_names.expense_preview',
  file_name: 'cc_statements.unmatched_expenses.column_names.file_name',
  total_amount: 'cc_statements.unmatched_expenses.column_names.total_amount',
  expense_date: 'cc_statements.unmatched_expenses.column_names.date',
  expense_currency_code: 'cc_statements.unmatched_expenses.column_names.currency',
  user: 'cc_statements.unmatched_expenses.column_names.user',
  restart_matching: 'cc_statements.uncategorized_expenses.column_names.restart_matching',
};

const UnmatchedExpensesTable = ({ data, unmatchedExpenses, refreshStatementData, isLoading, statementId }) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteExpenseConfirmation, setShowDeleteExpenseConfirmation] = useState(false);
  const [fieldsAreUpdating, setFieldsAreUpdating] = useState(false);
  const [restartingChecksums, setRestartingChecksums] = useState(new Set());

  const prepareTableData = (expenses) => {
    const tableData = expenses.map((expense) => ({
      checksum: expense?.checksum,
      expense_preview: <InvoicePreview key={`${expense?.checksum}-${expense?.json?.Name}`} file_name={expense?.json?.Name} currentInvoice={null} />,
      file_name: expense?.original_file_name || expense?.name || 'N/A',
      total_amount: fieldsAreUpdating ? (
        <LoadingSpinner data-testid='loading-spinner' />
      ) : (
        <FormTextField
          format='money'
          value={expense?.json?.TotalWithTax ? `${expense?.json.TotalWithTax.toFixed(2)}` : '0.00'}
          validateChange={(val) => {
            const parsed = Number.parseFloat(val);
            if (!Number.isFinite(parsed)) return; // ignore invalid input
            updateExpense({ ...expense, json: { ...expense?.json, TotalWithTax: parsed } });
          }}
        />
      ),
      expense_date: fieldsAreUpdating ? (
        <LoadingSpinner data-testid='loading-spinner' />
      ) : (
        <FormTextField
          format='date'
          value={expense?.json?.InvoiceDate || ''}
          validateChange={(val) => {
            updateExpense({ ...expense, json: { ...expense?.json, InvoiceDate: val } });
          }}
        />
      ),
      expense_currency_code: expense?.json?.Currency || '',
      user: (() => {
        const first = expense?.by_user?.first_name?.trim() || expense?.json?.by_user?.first_name?.trim();
        const last = expense?.by_user?.last_name?.trim() || expense?.json?.by_user?.last_name?.trim();
        const combined = [first, last].filter(Boolean).join(' ').trim();
        return combined || 'N/A';
      })(),
      restart_matching: (
        <button
          className='btn btn-sm btn-primary w-100 d-flex justify-content-center align-items-center'
          disabled={restartingChecksums.has(expense?.checksum)}
          onClick={() => restartMatching(expense?.checksum)}
        >
          {restartingChecksums.has(expense?.checksum) ? (
            <LoadingSpinner data-testid={`restart-spinner-${expense?._id}`} />
          ) : (
            <>
              <FontAwesomeIcon icon={faSync} className='mr-2' />
              {data.t.read('cc_statements.uncategorized_expenses.column_names.restart_matching')}
            </>
          )}
        </button>
      ),
      raw_expense: expense,
    }));

    return tableData;
  };

  const lastSelectionRef = useRef([]);
  const onSelectedRowsChange = useCallback((rows) => {
    if (!Array.isArray(rows)) return;
    const prev = lastSelectionRef.current;
    const sameLength = prev.length === rows.length;
    const sameContent =
      sameLength &&
      prev.every((r, i) => {
        const prevId = r?._id ?? r;
        const nextId = rows[i]?._id ?? rows[i];
        return prevId === nextId;
      });
    if (sameContent) return; // no logical change
    lastSelectionRef.current = rows;
    setSelectedRows(rows);
  }, []);

  const deleteSelectedExpenses = () => {
    const expense_checksums = selectedRows.map((exp) => exp.raw_expense.checksum);
    fetch(
      `/c_card/expenses/${statementId}`,
      'DELETE',
      { expense_checksums },
      (response) => {
        toast.success(data.t.read('cc_transactions.delete_successful') || 'Expenses deleted successfully');
        refreshStatementData();
        setSelectedRows([]);
      },
      (error) => {
        console.error('Error deleting expenses:', error);
        toast.error(data.t.read('cc_transactions.delete_failed') || 'Failed to delete expenses');
      }
    );
    setShowDeleteExpenseConfirmation(false);
  };

  const updateExpense = async (updatedExpense) => {
    setFieldsAreUpdating(true);

    updatedExpense.autoMatch = false;

    try {
      await fetch(
        `/c_card/expenses/${statementId}/${updatedExpense.checksum}`,
        'PUT',
        updatedExpense,
        () => {
          // Refresh statement data to get the latest expense data
          refreshStatementData();
        },
        (error) => {
          throw error;
        }
      );
    } catch {
      // Refresh on failure to ensure UI is in sync
      refreshStatementData();
    } finally {
      setFieldsAreUpdating(false);
    }
  };

  const restartMatching = async (expenseChecksum) => {
    // Add checksum to restarting set
    setRestartingChecksums((prev) => new Set([...prev, expenseChecksum]));

    const expenseChecksums = [expenseChecksum];

    try {
      await fetch(
        `/c_card/expenses/${statementId}/restart_matching`,
        'POST',
        { expense_checksums: expenseChecksums },
        () => {
          toast.success(data.t.read('cc_statements.restart_matching_successful') || 'Restarted matching successfully');
          refreshStatementData();
        },
        (error) => {
          throw error;
        }
      );
    } catch {
      toast.error(data.t.read('cc_statements.restart_matching_failed') || 'Failed to restart matching');
    } finally {
      // Remove checksum from restarting set
      setRestartingChecksums((prev) => {
        const next = new Set(prev);
        next.delete(expenseChecksum);
        return next;
      });
    }
  };

  const memoizedTableData = useMemo(() => prepareTableData(unmatchedExpenses), [unmatchedExpenses, restartingChecksums, fieldsAreUpdating]);

  if (!unmatchedExpenses || unmatchedExpenses.length === 0) {
    return <></>;
  }

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center mb-3 mt-2' style={{ minHeight: '30px' }}>
        <h3 className='m-0'>{data.t.read('cc_statements.unmatched_expenses.title')}</h3>
        {selectedRows && selectedRows.length > 0 && (
          <button className='btn btn-danger' onClick={() => setShowDeleteExpenseConfirmation(true)}>
            <FontAwesomeIcon icon={faTrash} className='mr-2' />
            {data.t.read('cc_transactions.delete_expenses')}
          </button>
        )}
      </div>
      <Table
        columnNames={columnNameKeys}
        tableData={memoizedTableData}
        searchable={false}
        isLoading={isLoading}
        selectableRows
        onRowSelect={onSelectedRowsChange}
        draggableRows
        getDraggableData={(row) => row.raw_expense}
      />
      {showDeleteExpenseConfirmation && (
        <Modal
          width='50vw'
          title={data.t.read('cc_transactions.delete_expenses')}
          cancelAction={() => setShowDeleteExpenseConfirmation(false)}
          okAction={deleteSelectedExpenses}
          okText={data.t.read('delete')}
          cancelText={data.t.read('cancel')}
        >
          <p>{`${data.t.read('cc_transactions.delete_expenses_confirm')} (${selectedRows.length})${data.t.read(
            'cc_transactions.delete_expenses_confirm2'
          )}`}</p>
        </Modal>
      )}

      <hr className='my-5' />
    </div>
  );
};

export default inject('data')(observer(UnmatchedExpensesTable));
